import React, { useState, useMemo, useRef } from "react";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import FeedBackService from "../../services/FeedbackService";

const FAQ_ITEMS = [
  {
    id: "sale-fast",
    category: "sale",
    categoryName: "Satış & Tahsilat",
    icon: "pi-shopping-cart",
    badgeClass: "bg-blue-50 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400",
    question: "Hızlı Satış nasıl yapılır ve fiş kesilir?",
    answer:
      "Üst menüden veya F2 kısayolu ile 'Hızlı Satış' sayfasına gidin. Barkod okutarak veya ürün adıyla aratarak sepete ekleyin. Nakit, Kredi Kartı veya Veresiye seçeneğini belirleyip 'Satışı Tamamla' butonuna basın. Fişiniz otomatik yazdırılacaktır."
  },
  {
    id: "customer-collection",
    category: "sale",
    categoryName: "Satış & Tahsilat",
    icon: "pi-wallet",
    badgeClass: "bg-blue-50 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400",
    question: "Müşteriden tahsilat nasıl alınır?",
    answer:
      "Müşteriler sayfasından ilgili müşterinin detayına girerek 'Tahsilat Ekle' butonuna basabilirsiniz. Ayrıca Ana Sayfadaki 'Bugünün İş Ajandası' kartında bekleyen alacakların yanındaki 'Tahsil Et' butonu ile tek tıkla tahsilat kaydedebilirsiniz."
  },
  {
    id: "sale-print",
    category: "sale",
    categoryName: "Satış & Tahsilat",
    icon: "pi-print",
    badgeClass: "bg-blue-50 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400",
    question: "Geçmiş bir satışın bilgi fişi tekrar nasıl yazdırılır?",
    answer:
      "Satışlar > Satış Listesi sayfasına gidin. İlgili satışın detayını açtığınızda sağ üst köşedeki 'Yazdır' butonu ile 80mm fiş veya A4 fatura formatında yeniden çıktı alabilirsiniz."
  },
  {
    id: "stock-critical",
    category: "stock",
    categoryName: "Stok & Ürün",
    icon: "pi-box",
    badgeClass: "bg-amber-50 dark:bg-amber-950/70 text-amber-600 dark:text-amber-400",
    question: "Kritik stok uyarısı nedir ve nasıl ayarlanır?",
    answer:
      "Ürün eklerken veya düzenlerken 'Kritik Stok Miktarı' belirleyebilirsiniz. Mağazanızdaki stok bu sayının altına düştüğünde sistem Ana Sayfada ve Stok Raporunda otomatik kırmızı bildirim verir."
  },
  {
    id: "stock-cost",
    category: "stock",
    categoryName: "Stok & Ürün",
    icon: "pi-tag",
    badgeClass: "bg-amber-50 dark:bg-amber-950/70 text-amber-600 dark:text-amber-400",
    question: "Ürün maliyet fiyatı ve kârlılık nasıl hesaplanır?",
    answer:
      "Ürün kartında girdiğiniz 'Maliyet Fiyatı' veya Tedarikçilerden girdiğiniz Alış Faturalarındaki fiyatlar baz alınır. Satış fiyatı ile maliyet arasındaki fark brüt kârınızı ve Ana Sayfadaki Net Kâr analizini oluşturur."
  },
  {
    id: "expense-add",
    category: "finance",
    categoryName: "Kasa & Giderler",
    icon: "pi-credit-card",
    badgeClass: "bg-rose-50 dark:bg-rose-950/70 text-rose-600 dark:text-rose-400",
    question: "İşletme masrafı (Kira, Fatura, Maaş) nasıl girilir?",
    answer:
      "Giderler > Gider Listesi menüsünden 'Yeni Gider' butonuna tıklayın. Gider kategorisi (Örn: Kira, Elektrik, Personel Avansı), ödeme yapılan Kasa/Banka hesabı ve tutarı girerek kaydedin."
  },
  {
    id: "account-transfer",
    category: "finance",
    categoryName: "Kasa & Giderler",
    icon: "pi-arrows-h",
    badgeClass: "bg-rose-50 dark:bg-rose-950/70 text-rose-600 dark:text-rose-400",
    question: "Kasadan bankaya (Hesaplar arası virman) nasıl yapılır?",
    answer:
      "Kasa & Banka menüsüne gidin. Kaynak hesabı seçip 'Transfer Yap / Virman' butonuna tıklayarak hedef banka hesabını ve aktarılan tutarı seçip transferi tamamlayın."
  },
  {
    id: "settlement",
    category: "current",
    categoryName: "Cari & Mahsuplaşma",
    icon: "pi-sync",
    badgeClass: "bg-purple-50 dark:bg-purple-950/70 text-purple-600 dark:text-purple-400",
    question: "Mahsuplaşma nedir ve nasıl kullanılır?",
    answer:
      "Bir firma sizin hem müşteriniz (alacağınız var) hem de tedarikçiniz (borcunuz var) ise, Mahsuplaşma modülü sayesinde iki bakiyeyi birbirine mahsup ederek nakit hareketi olmadan netleştirebilirsiniz."
  }
];

const SHORTCUTS = [
  { key: "F1", desc: "Ana Sayfaya (Dashboard) Git", tag: "Navigasyon", icon: "pi-home", colorBg: "bg-indigo-50 dark:bg-indigo-950/70 text-indigo-600 dark:text-indigo-400" },
  { key: "F2", desc: "Hızlı Satış Ekranını Aç / Kaydet", tag: "Satış", icon: "pi-shopping-cart", colorBg: "bg-blue-50 dark:bg-blue-950/70 text-blue-600 dark:text-blue-400" },
  { key: "F3", desc: "Müşteri Listesini Aç", tag: "Cari", icon: "pi-users", colorBg: "bg-purple-50 dark:bg-purple-950/70 text-purple-600 dark:text-purple-400" },
  { key: "F4", desc: "Tedarikçi Listesini Aç", tag: "Cari", icon: "pi-truck", colorBg: "bg-purple-50 dark:bg-purple-950/70 text-purple-600 dark:text-purple-400" },
  { key: "ESC", desc: "Açık Pencereyi / Modalı Kapat", tag: "Genel", icon: "pi-times", colorBg: "bg-rose-50 dark:bg-rose-950/70 text-rose-600 dark:text-rose-400" },
  { key: "Ctrl + K", desc: "Hızlı Arama Çubuğuna Odaklan", tag: "Arama", icon: "pi-search", colorBg: "bg-emerald-50 dark:bg-emerald-950/70 text-emerald-600 dark:text-emerald-400" },
  { key: "Ctrl + P", desc: "Sayfayı / Fişi Yazdır", tag: "Yazdırma", icon: "pi-print", colorBg: "bg-amber-50 dark:bg-amber-950/70 text-amber-600 dark:text-amber-400" },
  { key: "Tab", desc: "Form Alanları Arasında Geçiş", tag: "Form", icon: "pi-arrow-right", colorBg: "bg-slate-200/70 dark:bg-slate-700/60 text-slate-700 dark:text-slate-300" }
];

const FEEDBACK_TYPES = [
  { label: "Öneri & Fikir", value: "FEATURE", icon: "pi pi-lightbulb", color: "text-amber-500" },
  { label: "Hata Bildirimi", value: "BUG", icon: "pi pi-exclamation-circle", color: "text-rose-500" },
  { label: "Soru & Yardım", value: "SUPPORT", icon: "pi pi-question-circle", color: "text-blue-500" },
  { label: "Genel Görüş", value: "GENERAL", icon: "pi pi-comment", color: "text-indigo-500" }
];

const RELEASE_NOTES = [
  {
    version: "v2.4.0",
    date: "Ağustos 2026",
    badge: "Güncel Sürüm",
    badgeColor: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200/60",
    features: [
      "Bugünün İş Ajandası & Öncelikli Takip widget'ı eklendi (Tahsilatlar, Ödemeler ve Stok Uyarıları).",
      "Ajandayı tek tıkla Açıp/Kapatabilen modern gizleme kontrolü.",
      "Gerçek Net Kâr Analizi: Ürün maliyetleri ve giderler düşülerek hesaplanan kârlılık sistemi.",
      "Yardım & Destek Merkezi (Kullanım kılavuzu, kısayollar ve geri bildirim)."
    ]
  },
  {
    version: "v2.3.0",
    date: "Temmuz 2026",
    badge: "Önceki",
    badgeColor: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border-slate-200",
    features: [
      "Cari Hesap Mahsuplaşma modülü eklendi.",
      "Karanlık & Aydınlık Tema kişiselleştirme desteği.",
      "Hızlı Satış barkod tarama performans iyileştirmesi."
    ]
  }
];

const TABS = [
  {
    id: "faq",
    label: "Rehber & SSS",
    icon: "pi-compass",
    activeRing: "ring-2 ring-blue-500/30 dark:ring-blue-400/40",
    activeIconClass: "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-xs shadow-blue-500/40",
    inactiveIconClass: "bg-blue-100 text-blue-600 dark:bg-blue-950/80 dark:text-blue-400",
    activeTextClass: "text-blue-600 dark:text-blue-400"
  },
  {
    id: "shortcuts",
    label: "Kısayollar",
    icon: "pi-bolt",
    activeRing: "ring-2 ring-amber-500/30 dark:ring-amber-400/40",
    activeIconClass: "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-xs shadow-amber-500/40",
    inactiveIconClass: "bg-amber-100 text-amber-600 dark:bg-amber-950/80 dark:text-amber-400",
    activeTextClass: "text-amber-600 dark:text-amber-400"
  },
  {
    id: "feedback",
    label: "Geri Bildirim",
    icon: "pi-send",
    activeRing: "ring-2 ring-emerald-500/30 dark:ring-emerald-400/40",
    activeIconClass: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xs shadow-emerald-500/40",
    inactiveIconClass: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/80 dark:text-emerald-400",
    activeTextClass: "text-emerald-600 dark:text-emerald-400"
  },
  {
    id: "whatsnew",
    label: "Yenilikler",
    icon: "pi-star-fill",
    activeRing: "ring-2 ring-purple-500/30 dark:ring-purple-400/40",
    activeIconClass: "bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-xs shadow-purple-500/40",
    inactiveIconClass: "bg-purple-100 text-purple-600 dark:bg-purple-950/80 dark:text-purple-400",
    activeTextClass: "text-purple-600 dark:text-purple-400"
  }
];

export default function HelpCenterDrawer({ visible, onHide, initialTab = "faq" }) {
  const toast = useRef(null);
  const [activeTab, setActiveTab] = useState(initialTab || "faq");

  React.useEffect(() => {
    if (visible && initialTab) {
      setActiveTab(initialTab);
    }
  }, [visible, initialTab]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedFaqId, setExpandedFaqId] = useState(null);

  // Feedback State
  const [fbCategory, setFbCategory] = useState(FEEDBACK_TYPES[0]);
  const [fbMessage, setFbMessage] = useState("");
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbSuccess, setFbSuccess] = useState(false);

  const filteredFaq = useMemo(() => {
    return FAQ_ITEMS.filter((item) => {
      const matchesSearch =
        !searchQuery.trim() ||
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCat = selectedCategory === "all" || item.category === selectedCategory;

      return matchesSearch && matchesCat;
    });
  }, [searchQuery, selectedCategory]);

  const handleSendFeedback = async () => {
    if (!fbMessage.trim()) return;
    setFbSubmitting(true);
    try {
      await FeedBackService.sendFeedback({
        type: fbCategory.value,
        message: fbMessage.trim(),
        userAgent: navigator.userAgent
      });
      setFbSuccess(true);
      setFbMessage("");
      toast.current?.show({
        severity: "success",
        summary: "İletildi",
        detail: "Geri bildiriminiz için teşekkür ederiz!",
        life: 4000
      });
    } catch (err) {
      console.error("Feedback error:", err);
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Bildirim gönderilemedi. Lütfen tekrar deneyin.",
        life: 4000
      });
    } finally {
      setFbSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Sidebar
        visible={visible}
        onHide={onHide}
        position="right"
        className="!w-full sm:!w-[490px] border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
        header={
          <div className="flex items-center gap-3 w-full pr-4">
            <div
              style={{
                width: "40px",
                height: "40px",
                minWidth: "40px",
                minHeight: "40px",
                maxWidth: "40px",
                maxHeight: "40px"
              }}
              className="rounded-2xl bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0"
            >
              <i className="pi pi-question-circle text-lg" />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <h3 className="m-0 text-base font-black text-slate-900 dark:text-white truncate">
                Yardım & Destek Merkezi
              </h3>
              <p className="text-[11px] text-slate-400 font-bold m-0 mt-0.5 truncate">
                Kullanım rehberi, kısayollar ve geri bildirim
              </p>
            </div>
          </div>
        }
      >
        <div className="flex flex-col h-full overflow-hidden pb-4">
          {/* TAB NAVIGATION SEGMENTED CONTROL */}
          <div className="grid grid-cols-4 gap-1 mb-3.5 p-1 bg-slate-100/90 dark:bg-slate-800/90 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-inner shrink-0">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all duration-200 cursor-pointer border-none ${
                    isActive
                      ? `bg-white dark:bg-slate-900 shadow-md ${tab.activeRing}`
                      : "bg-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50"
                  }`}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      minWidth: "24px",
                      minHeight: "24px",
                      maxWidth: "24px",
                      maxHeight: "24px"
                    }}
                    className={`rounded-lg flex items-center justify-center mb-1 shrink-0 transition-all ${
                      isActive ? tab.activeIconClass : tab.inactiveIconClass
                    }`}
                  >
                    <i className={`pi ${tab.icon} text-[11px]`} />
                  </div>
                  <span
                    className={`leading-tight text-center whitespace-nowrap text-[10.5px] ${
                      isActive ? `font-black ${tab.activeTextClass}` : "font-bold text-slate-500 dark:text-slate-400"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* TAB 1: FAQ & QUICK GUIDE */}
          {activeTab === "faq" && (
            <div
              className="flex flex-col gap-3 flex-1 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar"
              style={{ scrollbarWidth: "thin" }}
            >
              {/* Search Bar */}
              <div className="relative shrink-0">
                <i className="pi pi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <InputText
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nasıl yapılır? (stok, satış, tahsilat, virman ara...)"
                  className="w-full !pl-9 !pr-8 !py-2.5 !rounded-2xl !text-xs !bg-slate-50 dark:!bg-slate-800/80 !border-slate-200/90 dark:!border-slate-700 !font-semibold focus:!ring-2 focus:!ring-indigo-500"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border-none bg-transparent cursor-pointer p-0 text-xs"
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>

              {/* Category Filter Pills */}
              <div
                className="flex items-center gap-1.5 overflow-x-auto pb-1 shrink-0"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {[
                  { id: "all", label: "Tümü" },
                  { id: "sale", label: "Satış & Fiş" },
                  { id: "stock", label: "Stok & Maliyet" },
                  { id: "finance", label: "Kasa & Gider" },
                  { id: "current", label: "Cari" }
                ].map((c) => {
                  const isSel = selectedCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCategory(c.id)}
                      className={`shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-black transition-all cursor-pointer border ${
                        isSel
                          ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-transparent shadow-xs"
                          : "bg-slate-100/90 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 border-slate-200/80 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              {/* FAQ Accordion List with Card Containers */}
              <div className="space-y-2.5 mt-0.5">
                {filteredFaq.length > 0 ? (
                  filteredFaq.map((faq) => {
                    const isExpanded = expandedFaqId === faq.id;
                    return (
                      <div
                        key={faq.id}
                        className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                          isExpanded
                            ? "bg-white dark:bg-slate-850 border-indigo-200 dark:border-indigo-900/60 shadow-md ring-1 ring-indigo-500/10"
                            : "bg-slate-50/60 dark:bg-slate-850/40 border-slate-200/80 dark:border-slate-800 hover:bg-white dark:hover:bg-slate-850 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-xs"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedFaqId(isExpanded ? null : faq.id)}
                          className="w-full p-3.5 text-left flex items-center justify-between gap-3 bg-transparent border-none cursor-pointer"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div
                              style={{
                                width: "34px",
                                height: "34px",
                                minWidth: "34px",
                                minHeight: "34px",
                                maxWidth: "34px",
                                maxHeight: "34px"
                              }}
                              className={`rounded-xl flex items-center justify-center shrink-0 shadow-2xs ${faq.badgeClass}`}
                            >
                              <i className={`pi ${faq.icon} text-xs`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block text-xs font-black text-slate-850 dark:text-white leading-tight">
                                {faq.question}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-0.5 block">
                                {faq.categoryName}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{ width: "24px", height: "24px", minWidth: "24px", minHeight: "24px" }}
                            className={`rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${
                              isExpanded
                                ? "bg-indigo-600 text-white rotate-180"
                                : "bg-slate-200/70 dark:bg-slate-700/60 text-slate-500"
                            }`}
                          >
                            <i className="pi pi-chevron-down text-[9px]" />
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-4 pt-1 text-xs font-semibold text-slate-700 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800/80 bg-white/50 dark:bg-slate-900/40">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="py-12 text-center text-slate-400">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-2.5 text-slate-400">
                      <i className="pi pi-search text-xl" />
                    </div>
                    <p className="text-xs font-black text-slate-700 dark:text-slate-300 m-0">
                      Aradığınız kriterde yardım konusu bulunamadı
                    </p>
                    <p className="text-[11px] text-slate-400 m-0 mt-1">
                      Sorunuzu "Geri Bildirim" sekmesinden bize kolayca iletebilirsiniz.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: KEYBOARD SHORTCUTS */}
          {activeTab === "shortcuts" && (
            <div
              className="flex flex-col gap-3 flex-1 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar"
              style={{ scrollbarWidth: "thin" }}
            >
              <div className="p-3.5 rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-transparent border border-amber-200/60 dark:border-amber-900/40 flex items-center gap-3 shrink-0">
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    minWidth: "36px",
                    minHeight: "36px",
                    maxWidth: "36px",
                    maxHeight: "36px"
                  }}
                  className="rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-xs shadow-amber-500/30"
                >
                  <i className="pi pi-bolt text-sm" />
                </div>
                <p className="text-xs font-bold text-amber-950 dark:text-amber-200 m-0 leading-tight">
                  KasamBenim'i daha hızlı ve pratik kullanmak için klavye kısayollarını kullanabilirsiniz.
                </p>
              </div>

              <div className="space-y-2 mt-1">
                {SHORTCUTS.map((sc, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850/40 hover:bg-white dark:hover:bg-slate-850 hover:border-slate-300 dark:hover:border-slate-700 flex items-center justify-between gap-3 shadow-2xs hover:shadow-xs transition-all"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          minWidth: "32px",
                          minHeight: "32px",
                          maxWidth: "32px",
                          maxHeight: "32px"
                        }}
                        className={`rounded-xl flex items-center justify-center text-xs shrink-0 shadow-2xs ${sc.colorBg}`}
                      >
                        <i className={`pi ${sc.icon}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <strong className="block text-xs font-black text-slate-850 dark:text-white leading-tight">
                          {sc.desc}
                        </strong>
                        <span className="text-[10px] font-bold text-slate-400 mt-0.5 block">{sc.tag}</span>
                      </div>
                    </div>

                    <kbd className="shrink-0 px-3 py-1 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-white font-mono text-xs font-black shadow-xs">
                      {sc.key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: FEEDBACK / BUG REPORT */}
          {activeTab === "feedback" && (
            <div
              className="flex flex-col gap-4 flex-1 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar"
              style={{ scrollbarWidth: "thin" }}
            >
              {fbSuccess ? (
                <div className="py-12 flex flex-col items-center justify-center text-center gap-3">
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      minWidth: "56px",
                      minHeight: "56px",
                      maxWidth: "56px",
                      maxHeight: "56px"
                    }}
                    className="rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-300 flex items-center justify-center text-2xl shadow-sm shrink-0"
                  >
                    <i className="pi pi-check-circle" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-slate-900 dark:text-white m-0">
                      Bildiriminiz Başarıyla İletildi!
                    </h4>
                    <p className="text-xs text-slate-500 font-semibold mt-1 m-0">
                      Görüş ve önerileriniz KasamBenim'i geliştirmemize yardımcı oluyor.
                    </p>
                  </div>
                  <Button
                    label="Yeni Bildirim Gönder"
                    icon="pi pi-plus"
                    onClick={() => setFbSuccess(false)}
                    className="!rounded-xl !bg-slate-900 hover:!bg-slate-800 !text-white !text-xs !font-black !py-2 !px-4 mt-2 !border-none cursor-pointer shadow-xs"
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-3.5">
                  <div className="p-3.5 rounded-2xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/40 text-xs font-bold text-blue-950 dark:text-blue-200">
                    💡 Yaşadığınız bir sorunu, eksik bulduğunuz bir özelliği veya geliştirme önerinizi iletebilirsiniz.
                  </div>

                  <div>
                    <label className="block text-xs font-black text-slate-700 dark:text-slate-300 mb-1.5">
                      Bildirim Türü
                    </label>
                    <Dropdown
                      value={fbCategory}
                      options={FEEDBACK_TYPES}
                      optionLabel="label"
                      onChange={(e) => setFbCategory(e.value)}
                      className="w-full !rounded-xl !text-xs !font-bold dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700"
                      itemTemplate={(opt) => (
                        <div className="flex items-center gap-2 py-0.5">
                          <i className={`${opt.icon} ${opt.color} text-xs`} />
                          <span className="font-bold text-xs">{opt.label}</span>
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-black text-slate-700 dark:text-slate-300">
                        Mesajınız / Açıklama
                      </label>
                      <span className="text-[10px] font-bold text-slate-400">
                        {fbMessage.length} / 1000
                      </span>
                    </div>
                    <InputTextarea
                      value={fbMessage}
                      onChange={(e) => e.target.value.length <= 1000 && setFbMessage(e.target.value)}
                      rows={5}
                      placeholder="Lütfen önerinizi veya karşılaştığınız durumu detaylı açıklayın..."
                      className="w-full !rounded-2xl !p-3 !text-xs !font-semibold !bg-slate-50 dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700 focus:!ring-2 focus:!ring-indigo-500"
                    />
                  </div>

                  <Button
                    label={fbSubmitting ? "Gönderiliyor..." : "Bildirimi İlet"}
                    icon={fbSubmitting ? "pi pi-spin pi-spinner" : "pi pi-send"}
                    disabled={!fbMessage.trim() || fbSubmitting}
                    onClick={handleSendFeedback}
                    className="!w-full !rounded-2xl !bg-indigo-600 hover:!bg-indigo-500 !text-white !font-black !text-xs !py-3 !border-none shadow-md shadow-indigo-600/30 transition-all cursor-pointer"
                  />
                </div>
              )}
            </div>
          )}

          {/* TAB 4: WHAT'S NEW / RELEASE NOTES */}
          {activeTab === "whatsnew" && (
            <div
              className="flex flex-col gap-3 flex-1 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar"
              style={{ scrollbarWidth: "thin" }}
            >
              {RELEASE_NOTES.map((rn, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850/50 shadow-2xs"
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black text-slate-900 dark:text-white">{rn.version}</span>
                      <span className="text-[10px] font-bold text-slate-400">• {rn.date}</span>
                    </div>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[9.5px] font-black border ${rn.badgeColor}`}
                    >
                      {rn.badge}
                    </span>
                  </div>

                  <ul className="space-y-1.5 m-0 pl-4 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    {rn.features.map((f, fIdx) => (
                      <li key={fIdx} className="leading-relaxed">
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </Sidebar>
    </>
  );
}
